import { useState } from 'react';
import { Plus, Trash2, UserCog } from 'lucide-react';
import { PageHeader, EmptyState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Avatar, Checkbox, Select } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import type { Team, UserRow } from '@/lib/types';
import { useDeleteTeam, useSaveTeam, useTeams, useUsers } from './api';

export function TeamsPage() {
  const { data: teams = [], isLoading } = useTeams();
  const [editing, setEditing] = useState<Team | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div>
      <PageHeader
        title="Teams"
        description="Named groups with a lead. Teams drive pipeline roll-up, ticket routing and the team permission scope."
        actions={
          <Can permission="admin.team.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New team
            </Button>
          </Can>
        }
      />

      {isLoading ? (
        <TableSkeleton />
      ) : teams.length === 0 ? (
        <EmptyState
          icon={<UserCog className="h-8 w-8" />}
          title="No teams yet"
          description="Group people so work can be routed and rolled up by team."
          action={
            <Can permission="admin.team.create">
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New team
              </Button>
            </Can>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <Card
              key={team.id}
              className="cursor-pointer p-4 transition-shadow hover:shadow-md"
              onClick={() => setEditing(team)}
            >
              <div className="font-medium">{team.name}</div>
              {team.description && (
                <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                  {team.description}
                </p>
              )}
              <div className="mt-3 text-xs text-muted-foreground">
                Lead: {team.lead?.name ?? 'Not set'}
              </div>
              <div className="mt-2 flex items-center gap-1">
                {team.members.slice(0, 6).map((member) => (
                  <Avatar key={member.id} name={member.name} size={24} />
                ))}
                {team.memberCount > 6 && (
                  <span className="ms-1 text-xs text-muted-foreground">
                    +{team.memberCount - 6}
                  </span>
                )}
                {team.memberCount === 0 && (
                  <span className="text-xs text-muted-foreground">No members yet</span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <TeamDialog
          team={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function TeamDialog({ team, onClose }: { team: Team | null; onClose: () => void }) {
  const save = useSaveTeam();
  const remove = useDeleteTeam();
  // One page of active people is enough to staff a team; the picker filters it.
  const { data: users } = useUsers({ limit: 100, status: 'ACTIVE' });
  const people: UserRow[] = users?.data ?? [];

  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [leadId, setLeadId] = useState(team?.lead?.id ?? '');
  const [memberIds, setMemberIds] = useState<string[]>(team?.members.map((m) => m.id) ?? []);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setMemberIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  const visible = people.filter((person) =>
    filter ? person.name.toLowerCase().includes(filter.toLowerCase()) : true,
  );

  const onSave = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        ...(team ? { id: team.id } : {}),
        name,
        description,
        leadId,
        memberIds,
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save this team.');
    }
  };

  const onDelete = async () => {
    if (!team) return;
    setError(null);
    try {
      await remove.mutateAsync(team.id);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not delete this team.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{team ? 'Edit team' : 'New team'}</DialogTitle>
          <DialogDescription>
            The lead is used for approvals and escalation; members define the team permission scope.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="team-name">Team name</Label>
            <Input
              id="team-name"
              value={name}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="team-description">Description</Label>
            <Input
              id="team-description"
              value={description}
              placeholder="Optional"
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="team-lead">Team lead</Label>
            <Select
              id="team-lead"
              value={leadId}
              onChange={(event) => setLeadId(event.target.value)}
            >
              <option value="">Not set</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Members ({memberIds.length})</Label>
            <Input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter people…"
              className="h-8"
            />
            <div className="scrollbar-thin max-h-52 space-y-0.5 overflow-y-auto rounded-md border p-1.5">
              {visible.map((person) => (
                <label
                  key={person.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md p-1.5 hover:bg-accent"
                >
                  <Checkbox
                    checked={memberIds.includes(person.id)}
                    onCheckedChange={() => toggle(person.id)}
                  />
                  <Avatar name={person.name} size={24} />
                  <span className="min-w-0 flex-1 truncate text-sm">{person.name}</span>
                  {person.designation && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {person.designation}
                    </span>
                  )}
                </label>
              ))}
              {visible.length === 0 && (
                <p className="p-3 text-center text-sm text-muted-foreground">Nobody matched.</p>
              )}
            </div>
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          {team && (
            <Can permission="admin.team.delete">
              <Button
                variant="ghost"
                onClick={onDelete}
                disabled={remove.isPending}
                className="me-auto"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
                Delete
              </Button>
            </Can>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={save.isPending || name.trim().length < 2}>
            {save.isPending ? 'Saving…' : team ? 'Save changes' : 'Create team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
